import {
  Alert,
  Button,
  Checkbox,
  Col,
  Input,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  notification,
} from 'antd';
import { PrinterOutlined, SaveOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import {
  CompanyInfo,
  CONDITION_LABEL,
  handoverComplete,
  HandoverInfo,
  normalizeHandover,
  QuoteData,
  QuoteLine,
} from '../calc';
import { equipmentApi, errorText, QuoteView } from '../api';
import ContractModal from './contract-modal';
import styles from '../style.module.scss';

interface Props {
  quote: QuoteView | null;
  data: QuoteData;
  company: CompanyInfo;
  /** Tên người đang đăng nhập — gợi ý tên KTV. */
  currentUserName?: string;
  onSaved: (quote: QuoteView) => void;
}

const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }) : '';

/**
 * Bàn giao & nghiệm thu ngày setup: KTV + Creator kiểm từng thiết bị, xác nhận hoạt động bình thường, in biên bản
 * cho hai bên ký; KTV mang bản giấy về Công ty kèm hợp đồng rồi tích "Đã nộp bản giấy". Lưu qua PUT /handover
 * (sửa được cả khi hợp đồng đã ký).
 */
const HandoverTab = ({ quote, data, company, currentUserName, onSaved }: Props) => {
  // Gói theo tháng / năm (game) không có vật để bàn giao.
  const lines = useMemo(
    () => data.lines.filter((l) => !['tháng', 'năm'].includes(l.unit || '')),
    [data.lines]
  );
  const init = (): HandoverInfo => {
    const h = normalizeHandover(data.handover ?? {}, data.lines);
    return {
      ...h,
      date: h.date || new Date().toISOString().slice(0, 10),
      technicianName: h.technicianName || currentUserName || '',
    };
  };
  const [h, setH] = useState<HandoverInfo>(init);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  // Đổi báo giá (hoặc vừa lưu xong) → nạp lại từ dữ liệu server.
  useEffect(
    () => setH(init()),
    // init đọc data/currentUserName mới nhất; chỉ nạp lại khi đổi báo giá hoặc server vừa ghi dấu giờ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quote?.id, data.handover?.confirmedAt, data.handover?.paperReceivedAt]
  );

  const set = (patch: Partial<HandoverInfo>) => {
    setH((x) => ({ ...x, ...patch }));
    setDirty(true);
  };
  const setCheck = (key: string, patch: Partial<HandoverInfo['checks'][string]>) =>
    set({ checks: { ...h.checks, [key]: { ...{ ok: false }, ...h.checks[key], ...patch } } });

  const allOk = lines.length > 0 && lines.every((l) => h.checks[l.key]?.ok);
  const complete = handoverComplete(h, lines);
  const cancelled = quote?.status === 'CANCELLED';

  const save = async () => {
    if (!quote) return;
    if ((h.creatorConfirmed || h.technicianConfirmed) && !allOk) {
      notification.warning({
        message: 'Còn thiết bị chưa tích "hoạt động bình thường"',
        description: 'Kiểm tra từng thiết bị trước khi hai bên xác nhận.',
      });
      return;
    }
    setSaving(true);
    try {
      const r = await equipmentApi.saveHandover(quote.id, h);
      onSaved(r.quote);
      setDirty(false);
      notification.success({ message: 'Đã lưu bàn giao' });
    } catch (e) {
      notification.error({ message: 'Chưa lưu được', description: errorText(e) });
    } finally {
      setSaving(false);
    }
  };

  if (!quote) return <Alert type="info" showIcon message="Lưu báo giá trước, rồi mới bàn giao." />;

  return (
    <Space direction="vertical" size={14} style={{ width: '100%' }}>
      {cancelled && <Alert type="warning" showIcon message="Báo giá đã huỷ — không bàn giao." />}
      {data.handover?.confirmedAt ? (
        <Alert
          type="success"
          showIcon
          message={`Đã bàn giao: Creator + KTV xác nhận lúc ${fmt(data.handover.confirmedAt)} (${
            data.handover.confirmedByName || ''
          })`}
          description={
            data.handover.paperReceived
              ? `Bản giấy đã nộp về Công ty lúc ${fmt(data.handover.paperReceivedAt)} (${
                  data.handover.paperReceivedByName || ''
                }).`
              : 'Chưa nộp bản giấy biên bản + hợp đồng về Công ty.'
          }
        />
      ) : (
        <Alert
          type="info"
          showIcon
          message="Ngày setup: kiểm từng thiết bị cùng Creator → tích xác nhận → In biên bản cho hai bên ký → mang bản giấy về Công ty kèm hợp đồng."
        />
      )}

      <Row gutter={[12, 10]}>
        <Col xs={24} md={6}>
          <div className={styles.fieldLabel}>Ngày bàn giao</div>
          <Input type="date" value={h.date} onChange={(e) => set({ date: e.target.value })} />
        </Col>
        <Col xs={24} md={10}>
          <div className={styles.fieldLabel}>Kỹ thuật viên</div>
          <Input
            value={h.technicianName}
            onChange={(e) => set({ technicianName: e.target.value })}
          />
        </Col>
        <Col xs={24} md={8}>
          <div className={styles.fieldLabel}>Điện thoại KTV</div>
          <Input
            value={h.technicianPhone}
            onChange={(e) => set({ technicianPhone: e.target.value })}
          />
        </Col>
      </Row>

      <Table<QuoteLine>
        rowKey="key"
        size="small"
        pagination={false}
        dataSource={lines}
        scroll={{ x: 820 }}
        columns={[
          {
            title: (
              <Checkbox
                checked={allOk}
                indeterminate={!allOk && lines.some((l) => h.checks[l.key]?.ok)}
                onChange={(e) =>
                  set({
                    checks: Object.fromEntries(
                      lines.map((l) => [l.key, { ...h.checks[l.key], ok: e.target.checked }])
                    ),
                  })
                }
              >
                Hoạt động bình thường
              </Checkbox>
            ),
            width: 190,
            render: (_, l) => (
              <Checkbox
                checked={!!h.checks[l.key]?.ok}
                onChange={(e) => setCheck(l.key, { ok: e.target.checked })}
              >
                Đã kiểm tra
              </Checkbox>
            ),
          },
          {
            title: 'Thiết bị',
            render: (_, l) => (
              <div>
                <b>{l.name}</b>
                <div style={{ fontSize: 12, opacity: 0.65 }}>
                  {[l.brand, CONDITION_LABEL[l.condition ?? 'NEW'], `SL ${l.qty}`]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
            ),
          },
          {
            title: 'Serial / mã tài sản',
            width: 180,
            render: (_, l) => (
              <Input
                value={h.checks[l.key]?.serial ?? l.serial ?? ''}
                placeholder="Ghi số serial"
                onChange={(e) => setCheck(l.key, { serial: e.target.value })}
              />
            ),
          },
          {
            title: 'Ghi chú',
            width: 200,
            render: (_, l) => (
              <Input
                value={h.checks[l.key]?.note ?? ''}
                placeholder="VD: trầy nhẹ góc"
                onChange={(e) => setCheck(l.key, { note: e.target.value })}
              />
            ),
          },
        ]}
      />

      <Space direction="vertical">
        <Space>
          <Switch checked={h.trained} onChange={(v) => set({ trained: v })} />
          Đã lắp đặt, cài phần mềm và hướng dẫn Creator sử dụng
        </Space>
        <Space>
          <Switch checked={h.liveTested} onChange={(v) => set({ liveTested: v })} />
          Đã LIVE thử thành công (hình, tiếng, kết nối ổn định)
        </Space>
        <Space>
          <Switch
            checked={h.technicianConfirmed}
            onChange={(v) => set({ technicianConfirmed: v })}
          />
          <b>KTV xác nhận</b> đã kiểm tra đầy đủ thiết bị
        </Space>
        <Space>
          <Switch checked={h.creatorConfirmed} onChange={(v) => set({ creatorConfirmed: v })} />
          <b>Creator xác nhận</b> đã nhận đủ, tất cả hoạt động bình thường
        </Space>
        <Space>
          <Switch checked={h.paperReceived} onChange={(v) => set({ paperReceived: v })} />
          Bản giấy biên bản + hợp đồng (có chữ ký) <b>đã nộp về Công ty</b>
        </Space>
      </Space>
      <div>
        <div className={styles.fieldLabel}>Ghi chú bàn giao</div>
        <Input.TextArea rows={2} value={h.note} onChange={(e) => set({ note: e.target.value })} />
      </div>

      <Space wrap>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={cancelled}
          onClick={save}
        >
          {dirty ? 'Lưu bàn giao' : 'Đã lưu bàn giao'}
        </Button>
        <Button icon={<PrinterOutlined />} onClick={() => setPrintOpen(true)}>
          In biên bản bàn giao
        </Button>
        {complete ? <Tag color="green">Đủ điều kiện bàn giao</Tag> : <Tag>Chưa đủ xác nhận</Tag>}
      </Space>

      <ContractModal
        kind="handover"
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        code={quote.code}
        data={{ ...data, handover: h }}
        company={company}
      />
    </Space>
  );
};

export default HandoverTab;
